import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';

import { Save, Trash2, ArrowLeft, Loader2, X, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { getAllSafetyRules, saveSafetyRule, deleteSafetyRule } from '../../../services/firestore/formularyService';
import type { SafetyRule, SafetyRuleSeverity, SafetyTriggerType } from '../../../types/formulary';

const EMPTY_RULE: SafetyRule = {
  id: '',
  name: '',
  severity: 'warn',
  triggerType: 'drug_interaction',
  matchMedications: [],
  matchConditions: [],
  matchAllergens: [],
  matchProductIds: [],
  affectedProductIds: [],
  message: '',
  clinicalNote: '',
  isActive: true,
  updatedAt: '',
};

const SEVERITIES: SafetyRuleSeverity[] = ['block', 'warn', 'info'];
const TRIGGER_TYPES: SafetyTriggerType[] = [
  'drug_interaction', 'condition', 'allergy', 'combination',
  'pregnancy', 'gender', 'age', 'duplicate',
];

const SEVERITY_COLORS: Record<SafetyRuleSeverity, string> = {
  block: 'text-critical',
  warn: 'text-yellow-600',
  info: 'text-blue-600',
};

export function SafetyRuleForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  const [rule, setRule] = useState<SafetyRule>({ ...EMPTY_RULE });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['basic', 'matching', 'affected', 'messaging'])
  );

  useEffect(() => {
    if (id) {
      loadRule(id);
    }
  }, [id]);

  async function loadRule(ruleId: string) {
    try {
      setLoading(true);
      const allRules = await getAllSafetyRules();
      const found = allRules.find((r) => r.id === ruleId);
      if (found) {
        setRule(found);
      } else {
        setToast('Rule not found');
        navigate('/admin/safety');
      }
    } catch (err) {
      setToast('Error loading rule: ' + (err instanceof Error ? err.message : 'Unknown'));
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!rule.id.trim()) {
      setToast('Rule ID is required');
      return;
    }
    if (!rule.name.trim()) {
      setToast('Rule name is required');
      return;
    }
    try {
      setSaving(true);
      await saveSafetyRule(rule);
      setToast('Safety rule saved successfully');
      if (isNew) {
        navigate(`/admin/safety/${rule.id}`, { replace: true });
      }
    } catch (err) {
      setToast('Error saving: ' + (err instanceof Error ? err.message : 'Unknown'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!id) return;
    if (!window.confirm(`Delete "${rule.name}"? This cannot be undone.`)) return;
    try {
      setDeleting(true);
      await deleteSafetyRule(id);
      setToast('Rule deleted');
      navigate('/admin/safety');
    } catch (err) {
      setToast('Error deleting: ' + (err instanceof Error ? err.message : 'Unknown'));
      setDeleting(false);
    }
  }

  function toggleSection(section: string) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  }

  function updateField<K extends keyof SafetyRule>(key: K, value: SafetyRule[K]) {
    setRule((prev) => ({ ...prev, [key]: value }));
  }

  function parseArray(value: string): string[] {
    return value.split(',').map((s) => s.trim()).filter(Boolean);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      {/* Toast */}
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed top-4 right-4 z-50 bg-bg-elevated border border-bg-tertiary rounded-lg px-4 py-3 text-base shadow-lg flex items-center gap-2"
        >
          <Check size={16} className="text-optimal" />
          {toast}
          <button onClick={() => setToast(null)} className="ml-2 text-text-muted hover:text-text-primary">
            <X size={14} />
          </button>
        </motion.div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/safety')} className="p-2 rounded-lg hover:bg-bg-elevated text-text-secondary">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="text-3xl font-display text-gold">{isNew ? 'New Safety Rule' : 'Edit Safety Rule'}</h2>
            {!isNew && <p className="text-base text-text-muted">ID: {rule.id}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-base font-medium transition-colors disabled:opacity-50"
            >
              {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
              Delete
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-accent hover:bg-accent/80 text-white rounded-lg text-base font-medium transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Rule
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Basic */}
        <FormSection title="Basic Info" id="basic" expanded={expandedSections.has('basic')} onToggle={toggleSection}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Rule ID" required>
              <input type="text" value={rule.id} onChange={(e) => updateField('id', e.target.value)} disabled={!isNew} placeholder="e.g. warfarin-blood-thinners" className="input-field" />
            </Field>
            <Field label="Name" required>
              <input type="text" value={rule.name} onChange={(e) => updateField('name', e.target.value)} placeholder="Rule name" className="input-field" />
            </Field>
            <Field label="Severity">
              <select value={rule.severity} onChange={(e) => updateField('severity', e.target.value as SafetyRuleSeverity)} className="input-field">
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
              <p className={`text-sm mt-1 ${SEVERITY_COLORS[rule.severity]}`}>
                {rule.severity === 'block' && 'Blocks the recommendation entirely'}
                {rule.severity === 'warn' && 'Shows a warning but allows recommendation'}
                {rule.severity === 'info' && 'Informational note for the provider'}
              </p>
            </Field>
            <Field label="Trigger Type">
              <select value={rule.triggerType} onChange={(e) => updateField('triggerType', e.target.value as SafetyTriggerType)} className="input-field">
                {TRIGGER_TYPES.map((t) => (
                  <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </Field>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rule.isActive}
                  onChange={(e) => updateField('isActive', e.target.checked)}
                  className="w-4 h-4 rounded border-bg-tertiary bg-bg-elevated text-accent focus:ring-accent"
                />
                <span className="text-base text-text-secondary">Active</span>
              </label>
            </div>
          </div>
        </FormSection>

        {/* Matching Criteria */}
        <FormSection title="Matching Criteria" id="matching" expanded={expandedSections.has('matching')} onToggle={toggleSection}>
          <div className="space-y-4">
            <p className="text-sm text-text-muted">Define what triggers this safety rule. Use comma-separated values. A rule fires when any match criteria is found.</p>
            <Field label="Match Medications (comma-separated, lowercase)">
              <input
                type="text"
                value={(rule.matchMedications || []).join(', ')}
                onChange={(e) => updateField('matchMedications', parseArray(e.target.value))}
                placeholder="e.g. warfarin, heparin, aspirin"
                className="input-field"
              />
            </Field>
            <Field label="Match Conditions (comma-separated, lowercase)">
              <input
                type="text"
                value={(rule.matchConditions || []).join(', ')}
                onChange={(e) => updateField('matchConditions', parseArray(e.target.value))}
                placeholder="e.g. pregnancy, liver disease, kidney disease"
                className="input-field"
              />
            </Field>
            <Field label="Match Allergens (comma-separated, lowercase)">
              <input
                type="text"
                value={(rule.matchAllergens || []).join(', ')}
                onChange={(e) => updateField('matchAllergens', parseArray(e.target.value))}
                placeholder="e.g. shellfish, soy, gluten"
                className="input-field"
              />
            </Field>
            <Field label="Match Product IDs (comma-separated)">
              <input
                type="text"
                value={(rule.matchProductIds || []).join(', ')}
                onChange={(e) => updateField('matchProductIds', parseArray(e.target.value))}
                placeholder="e.g. omega-3-fish-oil, vitamin-e"
                className="input-field"
              />
            </Field>
          </div>
        </FormSection>

        {/* Affected Products */}
        <FormSection title="Affected Products" id="affected" expanded={expandedSections.has('affected')} onToggle={toggleSection}>
          <div className="space-y-4">
            <p className="text-sm text-text-muted">Which products are affected by this rule. Leave empty for a global rule that applies to all products.</p>
            <Field label="Affected Product IDs (comma-separated)">
              <input
                type="text"
                value={rule.affectedProductIds.join(', ')}
                onChange={(e) => updateField('affectedProductIds', parseArray(e.target.value))}
                placeholder="e.g. fish-oil-omega-3, vitamin-k2 (empty = global rule)"
                className="input-field"
              />
            </Field>
            {rule.affectedProductIds.length === 0 && (
              <p className="text-sm text-gold">This is a global rule -- it applies to all products.</p>
            )}
          </div>
        </FormSection>

        {/* Messaging */}
        <FormSection title="Messaging" id="messaging" expanded={expandedSections.has('messaging')} onToggle={toggleSection}>
          <div className="space-y-4">
            <Field label="User-Facing Message" required>
              <textarea
                value={rule.message}
                onChange={(e) => updateField('message', e.target.value)}
                placeholder="Warning shown to the patient..."
                rows={3}
                className="input-field"
              />
            </Field>
            <Field label="Clinical Note (Provider-Facing)">
              <textarea
                value={rule.clinicalNote}
                onChange={(e) => updateField('clinicalNote', e.target.value)}
                placeholder="Internal note for providers..."
                rows={3}
                className="input-field"
              />
            </Field>
            {rule.updatedAt && (
              <Field label="Last Updated">
                <p className="text-base text-text-muted py-2">{new Date(rule.updatedAt).toLocaleString()}</p>
              </Field>
            )}
          </div>
        </FormSection>
      </div>

      {/* Bottom save bar */}
      <div className="sticky bottom-0 mt-6 py-4 bg-bg-deep border-t border-bg-tertiary flex items-center justify-end gap-3">
        <button onClick={() => navigate('/admin/safety')} className="px-4 py-2.5 text-base text-text-secondary hover:text-text-primary transition-colors">
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent hover:bg-accent/80 text-white rounded-lg text-base font-medium transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Save Rule
        </button>
      </div>
    </div>
  );
}

// --- Reusable sub-components ---

function FormSection({
  title,
  id,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  id: string;
  expanded: boolean;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-bg-elevated transition-colors"
      >
        <h3 className="text-base font-semibold text-text-secondary uppercase tracking-wider">{title}</h3>
        {expanded ? <ChevronUp size={16} className="text-text-muted" /> : <ChevronDown size={16} className="text-text-muted" />}
      </button>
      {expanded && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

function Field({
  label,
  required,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-text-muted mb-1.5">
        {label}
        {required && <span className="text-accent ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
